// Μία «προσπάθεια» χρέωσης/επιστροφής στο τερματικό, με τους κανόνες του server για το clientKey:
//  - ΕΝΑ κλειδί ανά προσπάθεια· ξανά πάτημα ή «Έλεγχος ξανά» της ίδιας προσπάθειας = ΙΔΙΟ κλειδί
//    (ο server επιστρέφει την ίδια εγγραφή, replay, και δεν χρεώνει δεύτερη φορά)·
//  - νέο κλειδί ΜΟΝΟ όταν ξέρουμε ότι δεν χρεώθηκε τίποτα (απόρριψη στο τερματικό / χωρίς υπογραφή)·
//  - ένα αίτημα τη φορά (διπλό πάτημα = ένα αίτημα)· «αβέβαιο» δεν ξαναστέλνεται ποτέ μόνο του.
//
// Αβέβαιη προσπάθεια (το τερματικό δεν απάντησε) μένει στη μνήμη της σελίδας ανά κράτηση/πελάτη/
// προκαταβολή: αν ο ταμίας κλείσει και ξανανοίξει το παράθυρο, συνεχίζει ΤΗΝ ΙΔΙΑ προσπάθεια (ίδιο
// κλειδί) — όχι νέα χρέωση πάνω σε μια που ίσως πέρασε.

import { useCallback, useEffect, useRef, useState } from 'react';
import { newClientKey, type ChargeOutcome } from './api';

export type AttemptPhase = 'idle' | 'charging' | 'ok' | 'declined' | 'failed' | 'uncertain' | 'error';

const MEMORY_MS = 30 * 60 * 1000;
const memory = new Map<string, { key: string; at: number; meta: unknown }>();

function recall(id: string | null | undefined) {
  if (!id) return null;
  const m = memory.get(id);
  if (!m) return null;
  if (Date.now() - m.at > MEMORY_MS) { memory.delete(id); return null; }
  return m;
}

/** Για τα tests. */
export const resetAttemptMemory = () => memory.clear();

export function useChargeAttempt<T, M = unknown>(resumeId?: string | null) {
  const [resumed] = useState(() => recall(resumeId));
  const keyRef = useRef<string | null>(resumed ? resumed.key : null);
  const inflight = useRef(false);
  const alive = useRef(true);
  const [phase, setPhase] = useState<AttemptPhase>(resumed ? 'uncertain' : 'idle');
  const [outcome, setOutcome] = useState<ChargeOutcome<T> | null>(
    resumed ? { kind: 'uncertain', message: null, code: 'resumed', id: null } : null,
  );

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  /**
   * Στέλνει την προσπάθεια. `memo.id` = σε ποιον ανήκει (κράτηση/τηλέφωνο/προκαταβολή): αν εκεί
   * υπάρχει αβέβαιη προσπάθεια, ξαναστέλνεται ΑΥΤΗ (το κλειδί της).
   */
  const run = useCallback(async (
    sendWithKey: (clientKey: string) => Promise<ChargeOutcome<T>>,
    memo?: { id: string | null; meta?: M },
  ) => {
    if (inflight.current) return null;
    inflight.current = true;
    if (!keyRef.current) keyRef.current = recall(memo?.id)?.key || newClientKey();
    const key = keyRef.current;
    setPhase('charging');
    let out: ChargeOutcome<T>;
    try {
      out = await sendWithKey(key);
    } catch {
      out = { kind: 'uncertain', message: null, code: 'network', id: null };
    } finally {
      inflight.current = false;
    }
    // Σίγουρα καμία χρέωση: η επόμενη προσπάθεια είναι ΝΕΑ.
    if (out.kind === 'declined' || out.kind === 'failed') keyRef.current = null;
    if (memo?.id) {
      if (out.kind === 'uncertain') memory.set(memo.id, { key, at: Date.now(), meta: memo.meta });
      // Άρνηση πριν από κάθε χρέωση (4xx) δεν λύνει μια αβέβαιη προσπάθεια: μένει στη μνήμη.
      else if (out.kind !== 'error') memory.delete(memo.id);
    }
    if (alive.current) {
      setOutcome(out);
      setPhase(out.kind);
    }
    return out;
  }, []);

  /** Μετά από απόρριψη/σφάλμα: πίσω στη φόρμα (το κλειδί μένει όπως το άφησε το αποτέλεσμα). */
  const back = useCallback(() => { setPhase('idle'); setOutcome(null); }, []);

  return {
    phase,
    outcome,
    run,
    back,
    charging: phase === 'charging',
    /** Στάλθηκε και δεν ξέρουμε το αποτέλεσμα (ή στέλνεται τώρα): η φόρμα κλειδώνει σε αυτήν. */
    locked: phase === 'uncertain' || phase === 'charging',
    /** Η αβέβαιη προσπάθεια που βρέθηκε στη μνήμη στο άνοιγμα (για να δείξει ποσό/τρόπο της). */
    resumed: (resumed ? resumed.meta : null) as M | null,
    /** Για διάγνωση / tests. */
    currentKey: () => keyRef.current,
  };
}
